import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import Drawer from "../../containment/Drawer/Drawer";
import {
  resolveInteractiveElement,
  resolveActiveProps,
} from "../../utils/resolveElement";
import css from "./NavigationDrawer.module.css";

const NavDrawerContext = React.createContext({
  variant: "dismissible",
  side: "left",
  label: "Navigation",
});

const NavigationDrawerRoot = ({
  variant = "dismissible",
  open,
  defaultOpen,
  onOpenChange,
  side = "left",
  label = "Navigation",
  className,
  children,
  ...rest
}) => {
  const ctx = React.useMemo(
    () => ({ variant, side, label }),
    [variant, side, label],
  );

  if (variant === "permanent") {
    return (
      <NavDrawerContext.Provider value={ctx}>
        <div className={className} {...rest}>
          {children}
        </div>
      </NavDrawerContext.Provider>
    );
  }

  return (
    <NavDrawerContext.Provider value={ctx}>
      <Drawer.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        modal={variant === "modal"}
        side={side}
        {...rest}
      >
        {children}
      </Drawer.Root>
    </NavDrawerContext.Provider>
  );
};

NavigationDrawerRoot.displayName = "NavigationDrawerRoot";

NavigationDrawerRoot.propTypes = {
  variant: PropTypes.oneOf(["permanent", "dismissible", "modal"]),
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  side: PropTypes.oneOf(["left", "right"]),
  label: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

const NavigationDrawerTrigger = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <Drawer.Trigger ref={ref} className={className} {...rest}>
      {children}
    </Drawer.Trigger>
  ),
);

NavigationDrawerTrigger.displayName = "NavigationDrawerTrigger";

NavigationDrawerTrigger.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const NavigationDrawerContent = React.forwardRef(
  ({ className, children, ...rest }, ref) => {
    const { variant, side, label } = React.useContext(NavDrawerContext);

    if (variant === "permanent") {
      return (
        <nav
          ref={ref}
          aria-label={label}
          className={clsx(css.permanent, className)}
          data-side={side}
          {...rest}
        >
          <div className={css.nav}>{children}</div>
        </nav>
      );
    }

    return (
      <Drawer.Popup ref={ref} size="sm" className={className} {...rest}>
        <nav aria-label={label} className={css.nav}>
          {children}
        </nav>
      </Drawer.Popup>
    );
  },
);

NavigationDrawerContent.displayName = "NavigationDrawerContent";

NavigationDrawerContent.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const NavigationDrawerItem = React.forwardRef(
  ({ href, active = false, icon, label, onClick, className, ...rest }, ref) => {
    const Element = resolveInteractiveElement({ href });
    const activeProps = resolveActiveProps({ href, active });

    return (
      <Element
        ref={ref}
        href={href}
        onClick={onClick}
        type={href ? undefined : "button"}
        className={clsx(css.item, className)}
        data-active={active || undefined}
        {...activeProps}
        {...rest}
      >
        {icon && <span className={css.itemIcon}>{icon}</span>}
        {label}
      </Element>
    );
  },
);

NavigationDrawerItem.displayName = "NavigationDrawerItem";

NavigationDrawerItem.propTypes = {
  href: PropTypes.string,
  active: PropTypes.bool,
  icon: PropTypes.node,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

const NavigationDrawerGroup = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={clsx(css.group, className)} {...rest}>
      {children}
    </div>
  ),
);

NavigationDrawerGroup.displayName = "NavigationDrawerGroup";

NavigationDrawerGroup.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const NavigationDrawerGroupLabel = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={clsx(css.groupLabel, className)} {...rest}>
      {children}
    </div>
  ),
);

NavigationDrawerGroupLabel.displayName = "NavigationDrawerGroupLabel";

NavigationDrawerGroupLabel.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const NavigationDrawerDivider = React.forwardRef(
  ({ className, ...rest }, ref) => (
    <hr ref={ref} className={clsx(css.divider, className)} {...rest} />
  ),
);

NavigationDrawerDivider.displayName = "NavigationDrawerDivider";

NavigationDrawerDivider.propTypes = {
  className: PropTypes.string,
};

const NavigationDrawer = {
  Root: NavigationDrawerRoot,
  Trigger: NavigationDrawerTrigger,
  Content: NavigationDrawerContent,
  Item: NavigationDrawerItem,
  Group: NavigationDrawerGroup,
  GroupLabel: NavigationDrawerGroupLabel,
  Divider: NavigationDrawerDivider,
};

export default NavigationDrawer;
